import { QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

/**
 * One cache for the whole app.
 *
 * The defaults below are chosen against how this product is actually read: a
 * FinOps console people leave open on a second monitor, showing figures a
 * provider restates once a day. They are not the library defaults, and each
 * departure is deliberate.
 */

/**
 * Cost figures move once a day, not once a second.
 *
 * A minute of staleness means walking down a drill-down and back up again is
 * instant instead of a spinner per step, which is the whole reason for the
 * cache. It is short enough that a sync landing while someone watches shows up
 * on their next navigation rather than requiring a reload.
 */
const STALE_TIME_MS = 60_000;

/**
 * Refetching every time the tab regains focus is the library default and it is
 * wrong here. This console gets left open beside a terminal, so every
 * alt-tab would fire the whole page's queries again -- and on AWS Cost Explorer
 * a request is a real charge. The staleness window above already covers
 * "someone came back after a while".
 */
const REFETCH_ON_FOCUS = false;

/**
 * A 4xx is an answer, not a hiccup.
 *
 * Retrying a 400 or a 403 three times produces three identical refusals, three
 * log lines, and a page that takes four round-trips to tell the reader what it
 * knew immediately. Only genuinely transient failures are worth a second
 * attempt, and one is enough to cover a dropped connection.
 */
function retry(failureCount: number, error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (typeof status === "number" && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: REFETCH_ON_FOCUS,
        retry,
      },
    },
  });
}

export const queryClient = createQueryClient();
